import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "./StatusBadge";
import { ExternalLink, PanelRightOpen, MousePointerClick } from "lucide-react";

const TYPE_LABEL = {
  customer: "Customer", quote: "Quotation", customer_po: "Customer PO", order: "Order", invoices: "Invoice",
  serial: "Device serial", assets: "Asset", product: "Product", shipments: "Shipment", fulfilment: "Fulfilment trace",
  tickets: "Ticket", device: "Device 360°", employee: "Employee", companies: "Company", contacts: "Contact", locations: "Location",
};
const PEEKABLE = new Set(["customer", "quote", "customer_po", "order", "invoices", "serial", "assets", "product"]);

const fmtMoney = (n, cur) => n == null || n === "" ? null
  : `${!cur || cur === "INR" ? "₹" : cur} ${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function usePeek(item) {
  const cache = useRef(new Map());
  const [state, setState] = useState({ data: null, loading: false });
  useEffect(() => {
    if (!item || !PEEKABLE.has(item.type)) { setState({ data: null, loading: false }); return; }
    const key = `${item.type}:${item.id}`;
    if (cache.current.has(key)) { setState({ data: cache.current.get(key), loading: false }); return; }
    setState({ data: null, loading: true });
    let live = true;
    const t = setTimeout(() => {
      api.get(`/global-search/peek/${item.type}/${encodeURIComponent(item.id)}`)
        .then(({ data }) => { cache.current.set(key, data); if (live) setState({ data, loading: false }); })
        .catch(() => live && setState({ data: null, loading: false }));
    }, 140);
    return () => { live = false; clearTimeout(t); };
  }, [item]);
  return state;
}

const Row = ({ label, value, testId }) => (
  <div className="flex items-start gap-3 text-[12px]" data-testid={testId}>
    <span className="w-24 shrink-0 text-[10px] uppercase tracking-wide text-slate-400 pt-0.5">{label}</span>
    <span className="text-slate-800 min-w-0 break-words">{String(value)}</span>
  </div>
);

export default function SearchPeekCard({ item, onOpen, onFullPreview, canFullPreview }) {
  const { data, loading } = usePeek(item);
  if (!item) {
    return (
      <div className="h-full grid place-items-center text-center px-6 text-[12px] text-slate-400" data-testid="oc-peek-empty">
        <div><MousePointerClick className="h-5 w-5 mx-auto mb-2 text-slate-300" />Highlight a result to preview it here</div>
      </div>
    );
  }
  const card = data || { title: item.title, subtitle: item.subtitle, status: item.status, dates: [], facts: item.meta != null ? [{ label: "Detail", value: item.meta }] : [] };
  const amount = fmtMoney(card.amount, card.currency);
  return (
    <div className="h-full flex flex-col os-fade-in" key={`${item.type}-${item.id}`} data-testid="oc-peek-card">
      <div className="px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400">{TYPE_LABEL[item.type] || "Record"}</div>
        <div className="mt-1 text-sm font-semibold text-slate-900 leading-snug break-words" data-testid="oc-peek-title">{card.title}</div>
        {card.subtitle && <div className="text-[12px] text-slate-500 truncate">{card.subtitle}</div>}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {card.status && <StatusBadge value={card.status} />}
          {amount && <span className="font-mono text-[15px] font-semibold text-slate-900" data-testid="oc-peek-amount">{amount}</span>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {loading && [...Array(5)].map((_, i) => <div key={i} className="h-3 rounded bg-slate-100 animate-pulse" />)}
        {!loading && card.dates?.map((d) => <Row key={d.label} label={d.label} value={d.value} testId={`oc-peek-date-${d.label.toLowerCase().replace(/\s+/g, "-")}`} />)}
        {!loading && card.facts?.map((f) => <Row key={f.label} label={f.label} value={f.value} testId={`oc-peek-fact-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />)}
      </div>
      <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2">
        {canFullPreview && (
          <button type="button" onClick={onFullPreview} data-testid="oc-peek-full-preview"
            className="h-8 px-3 rounded-md border border-slate-200 text-[12px] font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
            <PanelRightOpen className="h-3.5 w-3.5" /> Preview
          </button>
        )}
        <button type="button" onClick={() => onOpen(card.route || item.route)} data-testid="oc-peek-open"
          className="ml-auto h-8 px-3 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 inline-flex items-center gap-1.5">
          Open <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
